import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classes from './CategoryManageSheet.module.css';
import ConfirmDialog from '../UI/ConfirmDialog';
import useDragItem, { isTouchCapable } from '../../hooks/useDragItem';
import { CATEGORY_ICON_OPTIONS, getIconComponentByKey, getCategoryIconKey } from '../../config/categoryIcons';

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const PencilIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" stroke="#808383" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" stroke="#808383" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DragHandleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M4 7H20M4 12H20M4 17H20" stroke="#c8cccc" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const TrashIcon = ({ color = '#808383' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M3 6H21M8 6V4H16V6M19 6L18 20H6L5 6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 5V19M5 12H19" stroke="#808383" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Width of the red delete action revealed behind a row on swipe — also the
// clamp bound for swipeX and the snap-open threshold (half this).
const DELETE_REVEAL_WIDTH = 84;

const CategoryRow = ({
  category,
  iconKey,
  isRenaming,
  renameValue,
  onRenameChange,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onPickIcon,
  onDeleteRequest,
  onDragStart,
  onDragOver,
  onDragEnd,
}) => {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const swipeStartRef = useRef({ x: 0, y: 0, startSwipeX: 0 });
  const renameInputRef = useRef(null);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) renameInputRef.current.focus();
  }, [isRenaming]);

  const { cardRef, isDragOver, dragHandlers, dataAttrs } = useDragItem({
    id: category,
    name: category,
    onDragStart,
    onDragOver,
    onDragEnd,
  });

  // Split dragHandlers between two elements instead of spreading the whole
  // bundle on the row (as GearCard/NeedItem do for their own "whole card is
  // draggable" reorder) — drag INITIATION (native draggable + its own
  // dragstart/dragend, and touch long-press start/end) goes on the handle
  // only, below. Without this, it fights the swipe-to-delete gesture on
  // touch: swipe reacts to movement instantly, while the long-press needs
  // ~400ms of stillness first, so swipe always won before drag could ever
  // engage — reordering was effectively impossible on touch. Drop-target
  // handling (dragenter/dragover/dragleave/drop) stays on the whole row —
  // standard HTML5 DnD: the drag SOURCE and drop TARGETS are independent
  // elements — so dropping still works anywhere on another row, not just
  // its handle.
  const {
    draggable: handleDraggable,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onDragStart: handleNativeDragStart,
    onDragEnd: handleNativeDragEnd,
    onDragEnter, onDragOver: rowOnDragOver, onDragLeave, onDrop,
  } = dragHandlers;

  // Swipe-to-reveal-delete — touch/pen only (see the always-visible trash
  // button below for real desktop mice, which have no swipe gesture and
  // would otherwise have no way to reach delete at all: native HTML5 drag,
  // enabled on the same row for reorder, takes over mouse-drag entirely).
  const handlePointerDown = (e) => {
    if (!isTouchCapable) return;
    // Grabbing the handle means reorder, not swipe.
    if (e.target.closest(`.${classes.dragHandle}`)) return;
    // Without this, dragging fast enough (or far enough past the clamp) can
    // move the finger off the row's own (shifted) bounding box mid-swipe —
    // pointermove/pointerup then stop targeting this element entirely,
    // leaving `swiping` stuck true and the row's snap-back transition
    // permanently disabled.
    e.currentTarget.setPointerCapture(e.pointerId);
    swipeStartRef.current = { x: e.clientX, y: e.clientY, startSwipeX: swipeX };
    setSwiping(true);
  };
  const handlePointerMove = (e) => {
    if (!isTouchCapable || !swiping) return;
    const dx = e.clientX - swipeStartRef.current.x;
    const dy = e.clientY - swipeStartRef.current.y;
    // A mostly-vertical drag is a page scroll, not a swipe — leave it alone.
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) return;
    const next = Math.min(0, Math.max(-DELETE_REVEAL_WIDTH, swipeStartRef.current.startSwipeX + dx));
    setSwipeX(next);
  };
  const handlePointerUp = () => {
    if (!isTouchCapable) return;
    setSwiping(false);
    setSwipeX(prev => (prev < -DELETE_REVEAL_WIDTH / 2 ? -DELETE_REVEAL_WIDTH : 0));
  };

  // Swiped open, and the user taps the row again (not the delete button
  // itself, which sits outside this element) — close it instead of firing
  // whatever they tapped underneath, matching the usual swipe-list
  // convention of "tap elsewhere to dismiss".
  const handleContentClickCapture = (e) => {
    if (swipeX !== 0) {
      e.preventDefault();
      e.stopPropagation();
      setSwipeX(0);
    }
  };

  return (
    <div className={classes.rowWrapper}>
      <button
        className={classes.deleteAction}
        style={{ width: DELETE_REVEAL_WIDTH }}
        onClick={() => { setSwipeX(0); onDeleteRequest(category); }}
      >
        <TrashIcon color="#ffffff" />
        <span>Delete</span>
      </button>
      <div
        ref={cardRef}
        {...dataAttrs}
        onDragEnter={onDragEnter}
        onDragOver={rowOnDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`${classes.row} ${isDragOver ? classes.rowDragOver : ''}`}
        style={{ transform: `translateX(${swipeX}px)`, transition: swiping ? 'none' : 'transform 0.2s ease' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClickCapture={handleContentClickCapture}
      >
        <button
          className={classes.rowIconBtn}
          onClick={() => onPickIcon(category)}
          aria-label={`Change icon for ${category}`}
        >
          {React.createElement(getIconComponentByKey(iconKey), { color: 'var(--brand-primary, #2be494)' })}
        </button>

        {isRenaming ? (
          <input
            ref={renameInputRef}
            className={classes.renameInput}
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onBlur={onCommitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onCommitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); onCancelRename(); }
            }}
            spellCheck="false"
          />
        ) : (
          <span className={classes.rowName}>{category}</span>
        )}

        <button
          className={classes.rowIconBtnSmall}
          onClick={() => onStartRename(category)}
          aria-label={`Rename ${category}`}
        >
          <PencilIcon />
        </button>

        {!isTouchCapable && (
          <button
            className={classes.rowIconBtnSmall}
            onClick={() => onDeleteRequest(category)}
            aria-label={`Delete ${category}`}
          >
            <TrashIcon />
          </button>
        )}

        <span
          className={classes.dragHandle}
          aria-label={`Reorder ${category}`}
          draggable={handleDraggable}
          onDragStart={handleNativeDragStart}
          onDragEnd={handleNativeDragEnd}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <DragHandleIcon />
        </span>
      </div>
    </div>
  );
};

const IconPickerSheet = ({ category, currentKey, onPick, onClose }) => (
  ReactDOM.createPortal(
    <>
      <div className={classes.backdrop} onClick={onClose} />
      <div className={classes.pickerSheet} onClick={(e) => e.stopPropagation()}>
        <div className={classes.sheetHeader}>
          <span className={classes.sheetTitle}>Choose icon</span>
          <button className={classes.closeBtn} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className={classes.iconGrid}>
          {CATEGORY_ICON_OPTIONS.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`${classes.iconOption} ${key === currentKey ? classes.iconOptionActive : ''}`}
              onClick={() => onPick(category, key)}
              aria-label={label}
            >
              <Icon color={key === currentKey ? 'var(--brand-primary, #2be494)' : '#565959'} />
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  )
);

const CategoryManageSheet = ({
  categories = [],
  categoryIcons = {},
  onClose,
  onAddCategory,
  onRenameCategory,
  onDeleteCategory,
  onSetCategoryIcon,
  onReorderCategories,
}) => {
  const [renamingCategory, setRenamingCategory] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [iconPickerCategory, setIconPickerCategory] = useState(null);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Mirrors Checklist.js's own item drag-reorder state shape/logic (see
  // SectionItems there) — same dedup-on-unchanged-target and splice-based
  // reindex, just keyed on category name instead of item id.
  const [dragCat, setDragCat] = useState(null);
  const [dragOverCat, setDragOverCat] = useState(null);
  const [reordering, setReordering] = useState(false);

  const handleCatDragStart = (e, id) => setDragCat({ id });
  const handleCatDragOver = (e, id) => {
    e?.preventDefault();
    setDragOverCat(prev => (prev?.id === id ? prev : { id }));
  };
  const handleCatDragEnd = async (e) => {
    if (reordering || !dragCat || !dragOverCat || dragCat.id === dragOverCat.id) {
      setDragCat(null);
      setDragOverCat(null);
      return;
    }
    e?.preventDefault();
    setReordering(true);
    const reordered = [...categories];
    const dragIndex = reordered.indexOf(dragCat.id);
    const overIndex = reordered.indexOf(dragOverCat.id);
    if (dragIndex !== -1 && overIndex !== -1) {
      reordered.splice(dragIndex, 1);
      reordered.splice(overIndex, 0, dragCat.id);
      await onReorderCategories(reordered);
    }
    setDragCat(null);
    setDragOverCat(null);
    setReordering(false);
  };

  const startRename = (category) => {
    setRenamingCategory(category);
    setRenameValue(category);
  };
  const cancelRename = () => {
    setRenamingCategory(null);
    setRenameValue('');
  };
  const commitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== renamingCategory) {
      onRenameCategory(renamingCategory, trimmed);
    }
    setRenamingCategory(null);
    setRenameValue('');
  };

  const confirmDelete = async () => {
    setDeleting(true);
    await onDeleteCategory(pendingDeleteCategory);
    setDeleting(false);
    setPendingDeleteCategory(null);
  };

  return ReactDOM.createPortal(
    <>
      <div className={classes.backdrop} onClick={onClose} />
      <div className={classes.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={classes.sheetHeader}>
          <span className={classes.sheetTitle}>Categories</span>
          <button className={classes.closeBtn} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className={classes.rowList}>
          {categories.map((category) => (
            <CategoryRow
              key={category}
              category={category}
              iconKey={getCategoryIconKey(category, categoryIcons)}
              isRenaming={renamingCategory === category}
              renameValue={renameValue}
              onRenameChange={setRenameValue}
              onStartRename={startRename}
              onCommitRename={commitRename}
              onCancelRename={cancelRename}
              onPickIcon={setIconPickerCategory}
              onDeleteRequest={setPendingDeleteCategory}
              onDragStart={handleCatDragStart}
              onDragOver={handleCatDragOver}
              onDragEnd={handleCatDragEnd}
            />
          ))}

          <button className={classes.addRow} onClick={() => onAddCategory && onAddCategory()}>
            <span className={classes.addRowIcon}><PlusIcon /></span>
            <span>Add category</span>
          </button>
        </div>
      </div>

      {iconPickerCategory && (
        <IconPickerSheet
          category={iconPickerCategory}
          currentKey={getCategoryIconKey(iconPickerCategory, categoryIcons)}
          onPick={(category, key) => { onSetCategoryIcon(category, key); setIconPickerCategory(null); }}
          onClose={() => setIconPickerCategory(null)}
        />
      )}

      {pendingDeleteCategory && (
        <ConfirmDialog
          message={<>Delete <strong>"{pendingDeleteCategory}"</strong> category?</>}
          confirmLabel="Delete"
          working={deleting}
          onCancel={() => setPendingDeleteCategory(null)}
          onConfirm={confirmDelete}
        />
      )}
    </>,
    document.body
  );
};

export default CategoryManageSheet;
